import { useCallback, useMemo, useState } from "react";
import type { AgentUsageQuery } from "@common/schemas/domains/agentRuns/agent-usage.schema";
import { useOrganizationAgentUsageMonthly } from "@api/agentUsage.api";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";
import { useToday } from "@components/activityTimeline/useToday";
import { useOrganizationHistoryStart } from "@components/organizationView/activity/useOrganizationHistoryStart";
import { formatDate } from "@helpers/date";
import {
    AgentUsageSelection,
    toMonthStart,
    toPickerSelection,
    toUsageWindow,
    yearStart,
} from "./agentUsageRange.lib";

/**
 * Owns the usage page's two requests. The monthly request covers the whole
 * history with no `end`; the table request sends `end` only when the picked
 * range has a bounded end. The server fills a missing `end` with its today
 * and returns the resolved range, which is the chart's and the window's end.
 */
export function useAgentUsageFilters(organizationId: string) {
    const viewerToday = useToday();
    const { startAt } = useOrganizationHistoryStart(undefined);
    const historyStart = startAt
        ? toMonthStart(formatDate(startAt))
        : yearStart(viewerToday);
    const monthly = useOrganizationAgentUsageMonthly({
        organizationId,
        query: { start: historyStart },
    });
    const today = monthly.data?.range.end ?? viewerToday;

    const [selection, setSelection] = useState<AgentUsageSelection>(() => ({
        start: yearStart(viewerToday),
    }));
    const [search, setSearch] = useState<string | undefined>(undefined);
    const [previewWindow, setPreviewWindow] =
        useState<DensityChartWindow | null>(null);

    const window = useMemo(
        () => toUsageWindow({ selection, historyStart, today }),
        [selection, historyStart, today],
    );
    const tableQuery = useMemo<AgentUsageQuery>(
        () => ({
            start: window.start,
            ...(selection.end && { end: selection.end }),
        }),
        [window.start, selection.end],
    );

    const handlePickerChange = useCallback(
        (range: DateOnlyDateRange, isAllTime = false) =>
            setSelection(
                toPickerSelection({ range, isAllTime, historyStart, today }),
            ),
        [historyStart, today],
    );
    const handleWindowChange = useCallback(
        (next: DensityChartWindow) =>
            setSelection({ start: next.start, end: next.end }),
        [],
    );

    return {
        monthly,
        historyStart,
        today,
        selection,
        window,
        tableQuery,
        search,
        setSearch,
        previewWindow,
        setPreviewWindow,
        handlePickerChange,
        handleWindowChange,
    };
}
